import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Textarea } from 'components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { 
  ArrowLeft, MapPin, Star, Shield, Award, Clock, 
  MessageCircle, Calendar, DollarSign, User
} from 'lucide-react';
import { toast } from 'sonner';

export default function CaregiverProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [caregiver, setCaregiver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageOpen, setMessageOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');

  useEffect(() => {
    fetchCaregiver();
  }, [userId]);

  const fetchCaregiver = async () => {
    try {
      const response = await api.get(`/caregivers/${userId}`);
      setCaregiver(response.data);
    } catch (error) {
      toast.error('Failed to load caregiver profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      await api.post('/messages', {
        recipient_id: userId,
        content: messageText
      });
      toast.success('Message sent!');
      setMessageText('');
      setMessageOpen(false);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleSubmitReview = async () => {
    try {
      await api.post(`/caregivers/${userId}/review`, {
        rating: reviewRating,
        comment: reviewComment
      });
      toast.success('Review submitted!');
      setReviewComment('');
      setReviewOpen(false);
      fetchCaregiver();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  const getTrustBadge = (level) => {
    switch (level) {
      case 'trusted':
        return { icon: Award, label: 'Trusted Caregiver', color: 'badge-trusted text-white', desc: 'Earned through 50+ approved hours and excellent ratings' };
      case 'identity_verified':
        return { icon: Shield, label: 'Identity Verified', color: 'badge-verified text-white', desc: 'ID verified by ElderLink' };
      default:
        return { icon: Shield, label: 'Basic Verified', color: 'badge-basic text-white', desc: 'Email and phone verified' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!caregiver) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="text-center p-8">
          <User className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Caregiver Not Found</h2>
          <Button className="mt-4" onClick={() => navigate('/caregivers')}>Back to Directory</Button>
        </Card>
      </div>
    );
  }

  const badge = getTrustBadge(caregiver.trust_level);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="caregiver-profile">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/caregivers')} data-testid="back-to-directory">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Caregiver Profile</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Profile Header */}
        <Card className="mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-24 h-24 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
                {caregiver.picture ? (
                  <img src={caregiver.picture} alt={caregiver.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-teal-100 text-teal-700 text-3xl font-bold">
                    {caregiver.name?.[0] || '?'}
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">{caregiver.name}</h2>
                    {caregiver.city && (
                      <p className="text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-4 h-4" />
                        {caregiver.city}, {caregiver.state}
                      </p>
                    )}
                    <div className="mt-3">
                      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${badge.color}`}>
                        <badge.icon className="w-4 h-4" />
                        {badge.label}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{badge.desc}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="message-btn">
                          <MessageCircle className="w-4 h-4" />
                          Message
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Message {caregiver.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 pt-4">
                          <Textarea
                            placeholder="Introduce yourself and describe your care needs..."
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            className="min-h-[120px]"
                            data-testid="message-textarea"
                          />
                          <Button
                            onClick={handleSendMessage}
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="send-message-btn"
                          >
                            Send Message
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-100">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <span className="text-xl font-bold text-slate-900">{caregiver.rating?.toFixed(1) || '-'}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{caregiver.review_count || 0} reviews</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Clock className="w-5 h-5 text-teal-500" />
                      <span className="text-xl font-bold text-slate-900">{caregiver.total_hours || 0}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">hours worked</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <span className="text-xl font-bold text-slate-900">{caregiver.hourly_rate || '-'}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">per hour</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {/* About */}
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">About</CardTitle>
              </CardHeader>
              <CardContent>
                {caregiver.bio ? (
                  <p className="text-slate-600 leading-relaxed">{caregiver.bio}</p>
                ) : (
                  <p className="text-slate-400 italic">No bio provided</p>
                )}
              </CardContent>
            </Card>

            {/* Services */}
            <Card>
              <CardHeader>
                <CardTitle className="font-['Manrope']">Services Offered</CardTitle>
              </CardHeader>
              <CardContent>
                {caregiver.services?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {caregiver.services.map((service) => (
                      <Badge key={service} className="bg-teal-100 text-teal-700 hover:bg-teal-200">
                        {service}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 italic">No services listed</p>
                )}
              </CardContent>
            </Card>

            {/* Languages */}
            {caregiver.languages?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Manrope']">Languages Spoken</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {caregiver.languages.map((lang) => (
                      <Badge key={lang} className="bg-blue-100 text-blue-700 hover:bg-blue-200">
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-['Manrope']">Reviews</CardTitle>
                {user?.role === 'family_caregiver' && (
                  <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="write-review-btn">
                        Write Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Review {caregiver.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Rating</label>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <button
                                key={star}
                                onClick={() => setReviewRating(star)}
                                className="focus:outline-none"
                              >
                                <Star
                                  className={`w-8 h-8 ${
                                    star <= reviewRating
                                      ? 'fill-amber-400 text-amber-400'
                                      : 'text-slate-300'
                                  }`}
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Comment</label>
                          <Textarea
                            placeholder="Share your experience..."
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            className="min-h-[100px]"
                          />
                        </div>
                        <Button
                          onClick={handleSubmitReview}
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                        >
                          Submit Review
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardHeader>
              <CardContent>
                {caregiver.reviews?.length > 0 ? (
                  <div className="space-y-4">
                    {caregiver.reviews.map((review, index) => (
                      <div key={review.review_id || index} className="pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-4 h-4 ${
                                  star <= review.rating
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-slate-300'
                                }`}
                              />
                            ))}
                          </div>
                          <span className="text-sm text-slate-500">by {review.reviewer_name}</span>
                        </div>
                        {review.comment && (
                          <p className="text-slate-600 text-sm">{review.comment}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(review.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 italic">No reviews yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Experience */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">Experience</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Years of experience</span>
                    <span className="font-medium text-slate-900">{caregiver.experience_years || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Background check</span>
                    <Badge variant={caregiver.background_check ? 'default' : 'secondary'}>
                      {caregiver.background_check ? 'Completed' : 'Pending'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Code of conduct</span>
                    <Badge variant={caregiver.code_of_conduct_accepted ? 'default' : 'secondary'}>
                      {caregiver.code_of_conduct_accepted ? 'Accepted' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Availability */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope']">Availability</CardTitle>
              </CardHeader>
              <CardContent>
                {caregiver.availability && Object.keys(caregiver.availability).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(caregiver.availability).map(([day, available]) => (
                      <div key={day} className="flex items-center justify-between">
                        <span className="text-slate-500 capitalize">{day}</span>
                        <Badge variant={available ? 'default' : 'secondary'}>
                          {available ? 'Available' : 'Unavailable'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 italic text-sm">Contact for availability</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
